import {Component, HostListener, OnInit, Optional} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {TRANSLATION_CRUD_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';
import {WorkflowService} from '../../services/workflow.service';
import {buildConditionVariables} from '../conditions/visual/workflow-condition-model';
import {mappingNameIssue, mappingUsage, sourceStage, suggestMappingName, VariableSource, variableSources, WorkflowMapping} from './workflow-variables';

export interface WorkflowVariableDialogComponent extends Translatable {}

/** Chooses a source before naming a mapping and keeps all edits detached until Apply. */
@Component({selector: 'app-workflow-variable-dialog', templateUrl: './workflow-variable-dialog.component.html',
  styleUrls: ['../conditions/visual/workflow-condition-dialog.component.css', './workflow-variable-dialog.component.css']})
@Translatable({scope: TRANSLATION_CRUD_SCOPE, keys: KEYS})
export class WorkflowVariableDialogComponent implements OnInit {
  public workflow: any;
  public original: WorkflowMapping;
  public customFields: any[] = [];
  public questionnaires: any[] = [];
  public locale = 'de_DE';
  public deleting = false;
  public inCondition = false;
  public selected: VariableSource;
  public name = '';
  private suggestedName = '';
  public choosing = true;
  public query = '';
  public category = '';
  public loading = false;
  public failed = false;
  public sources: VariableSource[] = [];

  /** Injects the full questionnaire loader and modal controller. */
  public constructor(public activeModal: NgbActiveModal, private service: WorkflowService,
    @Optional() private translationService: TranslationService) {}

  /** Restores an existing source immediately and then loads available full definitions. */
  public ngOnInit(): void {
    if (this.original) {
      this.name = this.original.name;
      this.choosing = false;
      this.selected = {...buildConditionVariables({variables: [this.original]}, this.questionnaires,
        this.customFields, this.locale, text => this._(text))[0], target: this.original.target};
    }
    void this.load();
  }

  /** Loads only questionnaires assigned to this workflow and offers an explicit retry on failure. */
  public async load(): Promise<void> {
    this.loading = true;
    this.failed = false;
    const ids = Array.from(new Set<string>((this.workflow.stages || []).flatMap(stage => stage.questionnaires || [])));
    const results = await Promise.all(ids.map(async id => {
      const known = this.questionnaires.find(questionnaire => questionnaire.id === id && Array.isArray(questionnaire.questions));
      if (known) { return known; }
      try { return await this.service.loadQuestionnaire(id); }
      catch (error) { this.failed = true; console.warn('Could not load variable source', id, error); return null; }
    }));
    this.questionnaires = results.filter(Boolean);
    this.sources = variableSources(this.questionnaires, this.customFields, this.locale, text => this._(text));
    if (this.selected) {
      const fresh = this.sources.find(source => this.sameSource(source.target, this.selected.target));
      if (fresh) { this.selected = {...fresh, target: this.selected.target}; }
    }
    this.loading = false;
  }

  /** Compares source identity independently of cached formatting hints. */
  public sameSource(first: WorkflowMapping['target'], second: WorkflowMapping['target']): boolean {
    return first.type === second.type && (first.id || '') === (second.id || '') && first.variable === second.variable;
  }

  /** Searches friendly names, questionnaire titles and technical paths together. */
  public get matches(): VariableSource[] {
    const terms = this.query.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
    return this.sources.filter(source => (!this.category || source.group === this.category) &&
      terms.every(term => `${source.label} ${source.detail} ${source.target.variable}`.toLocaleLowerCase().includes(term)));
  }

  /** Selects a source while preserving an existing mapping name or a deliberate new name. */
  public select(source: VariableSource): void {
    this.selected = source;
    if (!this.original && (!this.name || this.name === this.suggestedName)) {
      this.name = this.suggestedName = suggestMappingName(source, this.workflow.variables || []);
    }
    this.choosing = false;
  }

  /** Explains the earliest configured availability of a questionnaire result. */
  public availability(source: VariableSource): string {
    const index = sourceStage(this.workflow, source.target);
    return source.target.type === 'survey' ? this._('Von Beginn an verfügbar') : index < 0 ?
      this._('Quellfragebogen ist keinem Schritt zugeordnet') : this._('Nach Schritt') + ' ' + (index + 1);
  }

  /** Lists the actual step names whose conditions reference this mapping. */
  public get usages(): string[] {
    return this.original ? mappingUsage(this.workflow, this.original.name).indices.map(index =>
      this._('Schritt') + ' ' + (index + 1) + ': ' + (this.workflow.stages[index].name?.[this.locale] ||
        this.workflow.stages[index].name?.de_DE || this._('Ohne Namen'))) : [];
  }

  /** Blocks operations that would leave references broken or require guessing unsupported syntax. */
  public get issue(): string {
    const usage = this.original && mappingUsage(this.workflow, this.original.name);
    if (this.deleting) {
      return usage?.unknown ? 'Nicht alle Bedingungen sind lesbar. Die Variable kann nicht sicher entfernt werden.' :
        usage?.indices.length ? 'Ändern Sie zuerst die Bedingungen, die diese Variable verwenden.' : '';
    }
    const nameIssue = mappingNameIssue(this.name, this.workflow.variables || [], this.original?.name);
    if (nameIssue) { return nameIssue; }
    if (this.original && this.name !== this.original.name && usage.unknown) {
      return 'Nicht alle Bedingungen sind lesbar. Die Variable kann nicht sicher umbenannt werden.';
    }
    if (!this.selected) { return 'Bitte wählen Sie eine Quelle.'; }
    if (!this.sources.some(source => this.sameSource(source.target, this.selected.target)) && !this.loading) {
      return 'Diese Quelle ist nicht verfügbar. Bitte wählen Sie eine andere Quelle.';
    }
    return '';
  }

  /** Returns a mapping without writing to the workflow behind the dialog. */
  public save(): void {
    if (this.issue || this.loading) { return; }
    if (this.deleting) { this.activeModal.close({variable: null}); return; }
    const target = this.original && this.sameSource(this.original.target, this.selected.target) ?
      this.original.target : this.selected.target;
    const variable = {...this.original, name: this.name, target: JSON.parse(JSON.stringify(target))};
    this.activeModal.close({variable, descriptor: {...this.selected, name: this.name}});
  }

  /** Keeps global save shortcuts from persisting an unfinished mapping draft. */
  @HostListener('keydown', ['$event'])
  public keydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') { event.preventDefault(); event.stopPropagation(); }
  }
}
